import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BuyerIntroEmailDownloadDto,
  type BuyerIntroEmailStateDto,
} from "@/api/buyers";
import { Button, useToast } from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";

/**
 * The specialist's own mail client — the second half of the dual send.
 *
 * <p>🔴 <b>Fetching the copy RECORDS THE SEND.</b> That is the point, not a
 * side effect: both paths converge on one recorder, so a specialist who prefers
 * their own mailbox is not invisible in the funnel and the pipeline never
 * reports a contacted buyer as never contacted. The honest cost — fetch the
 * copy, then change your mind, and you have a stamp you did not earn — is
 * accepted because this stamp drives no allocation, no entitlement and no
 * money.</p>
 *
 * <p>🔴 <b>So it is never fetched speculatively.</b> The modal opens on an
 * explanation and the request is a MUTATION behind an explicit button — never a
 * `useQuery`, which would re-record the contact on any refetch or retry.</p>
 */
export function IntroEmailCopyModal({
  submissionId,
  email,
  includeListingLink,
  onRecorded,
  onClose,
}: {
  submissionId: string;
  email: BuyerIntroEmailStateDto;
  /** R3 — the panel's send-time choice. The server composes the copy with the
   *  line after the message, exactly as a Phoenix send would carry it. */
  includeListingLink: boolean;
  /** Called once taking the copy has recorded the send — the tab's collapse
   *  after a send (J6). */
  onRecorded?: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [copy, setCopy] = useState<BuyerIntroEmailDownloadDto | null>(null);

  const fetchCopy = useMutation({
    mutationFn: () => buyerSubmissionsApi.getIntroEmailDownload(submissionId, includeListingLink),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      // Taking the copy stamps the buyer's contact clocks too.
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      setCopy(d);
      onRecorded?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Send the intro email yourself"
      size="lg"
      footer={
        <div className="flex justify-end gap-space-3">
          <Button variant="secondary" onClick={onClose}>
            {copy ? "Done" : "Cancel"}
          </Button>
          {copy ? null : (
            <Button
              onClick={() => fetchCopy.mutate()}
              disabled={fetchCopy.isPending}
              data-testid="intro-email-copy-confirm"
            >
              {fetchCopy.isPending ? "Preparing…" : "Get the copy and record it as sent"}
            </Button>
          )}
        </div>
      }
    >
      {copy ? (
        <div className="grid gap-space-4">
          <p className="text-small text-onyx m-0">
            Recorded as sent. Paste this into your own email and send it to the
            buyer.
          </p>

          <CopyField label="To" value={copy.toEmail} />
          <CopyField label="Subject" value={copy.subject} />
          <CopyField label="Message" value={copy.body} multiline />

          <p className="text-caption text-gray-mid m-0">
            No signature is included — your own email will add yours.
          </p>
        </div>
      ) : (
        <div className="grid gap-space-4">
          <p className="text-small text-onyx m-0">
            Phoenix will not send anything — you will, from your own email to{" "}
            <strong>{email.buyerName}</strong>
            {email.buyerEmail ? (
              <>
                {" "}
                at <strong>{email.buyerEmail}</strong>
              </>
            ) : null}
            .
          </p>
          <p className="text-small text-status-warning m-0">
            Taking the copy records this intro email as sent, exactly as sending
            from Phoenix does — so the buyer does not look uncontacted. It does
            not change the submission&apos;s status.
          </p>
        </div>
      )}
    </Modal>
  );
}

/** Also used by the combined intro's copy modal, so the two own-client copies
 *  read and behave the same. */
export function CopyField({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string | null;
  multiline?: boolean;
}) {
  const toast = useToast();

  if (!value) {
    return (
      <div className="grid gap-space-1">
        <span className="text-caption text-gray-mid uppercase tracking-wider">
          {label}
        </span>
        <p className="text-small text-gray-mid m-0">
          Not on file — use the address you have for this buyer.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-space-2">
      <div className="flex items-center justify-between gap-space-2">
        <span className="text-caption text-gray-mid uppercase tracking-wider">
          {label}
        </span>
        <Button
          size="sm"
          variant="text"
          onClick={() => void writeToClipboard(value, label, toast)}
        >
          Copy {label.toLowerCase()}
        </Button>
      </div>
      <div
        className={
          multiline
            ? "max-h-72 overflow-y-auto rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
            : "rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
        }
      >
        <p className="text-small text-onyx whitespace-pre-wrap m-0">{value}</p>
      </div>
    </div>
  );
}

/** The clipboard API is unavailable over plain HTTP and in some embedded
 *  browsers, so a failure is reported rather than swallowed — the text is on
 *  screen and can be selected by hand. */
async function writeToClipboard(
  value: string,
  label: string,
  toast: ReturnType<typeof useToast>,
) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied.`);
  } catch {
    toast.error(`Couldn't copy the ${label.toLowerCase()}. Select it and copy by hand.`);
  }
}
